import { Alert, Button, Form, Input, Modal, Typography, message as antdMessage } from 'antd';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { feedbackActions, selectFeedbackError, selectFeedbackOpen, selectFeedbackStatus } from '../../store/slices/feedback';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { feedbackService, type FeedbackPayload } from '../../services/feedback';

const { TextArea } = Input;
const { Text } = Typography;

export const FeedbackModal = memo(() => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectFeedbackOpen);
  const status = useAppSelector(selectFeedbackStatus);
  const error = useAppSelector(selectFeedbackError);
  const authUser = useAppSelector((state) => state.auth.user);
  const location = useLocation();
  const authedEmail = authUser?.email?.trim() || '';
  const hasAuthedEmail = authedEmail.length > 0;
  const isAuthed = !!authUser;

  useEffect(() => {
    if (open) {
      dispatch(feedbackActions.resetStatus());
      if (hasAuthedEmail) {
        form.setFieldsValue({ email: authedEmail });
      }
    }
  }, [authedEmail, dispatch, form, hasAuthedEmail, open]);

  const closeModal = useCallback(() => {
    if (status === 'submitting') return;
    dispatch(feedbackActions.closeModal());
    form.resetFields();
  }, [dispatch, form, status]);

  const submitFeedback = useCallback(
    async (values: { message: string; email?: string }) => {
      const trimmedMessage = values.message?.trim() ?? '';
      const displayName = authUser?.display_name?.trim();
      const messageWithContext =
        displayName && trimmedMessage
          ? `${trimmedMessage}\n\n---\nUser display name: ${displayName}`
          : trimmedMessage;
      const email = authedEmail || values.email?.trim() || undefined;
      const payload: FeedbackPayload = {
        message: messageWithContext,
        email,
        route: location.pathname,
        userId: authUser?.id ?? undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };

      dispatch(feedbackActions.startSubmit());
      try {
        const response = await feedbackService.submitFeedback(payload);
        if (!response.success) {
          throw new Error(response.error || 'Failed to send feedback');
        }
        dispatch(feedbackActions.submitSuccess({ id: response.id ?? null }));
        antdMessage.success('Thanks for your feedback!');
        closeModal();
      } catch (err: any) {
        const apiMessage =
          err?.response?.data?.error ??
          err?.message ??
          (typeof err === 'string' ? err : 'Failed to send feedback');
        dispatch(feedbackActions.submitFailure(apiMessage));
        const isRateLimited = apiMessage?.toLowerCase?.().includes('too many');
        antdMessage.error(
          isRateLimited
            ? 'You are sending feedback too quickly. Please try again in a moment.'
            : apiMessage
        );
      }
    },
    [authUser?.id, closeModal, dispatch, location.pathname]
  );

  const okText = useMemo(() => {
    if (status === 'submitting') return 'Sending...';
    return 'Send';
  }, [status]);

  return (
    <Modal
      centered
      width={560}
      open={open}
      onCancel={closeModal}
      footer={null}
      className='feedback-modal'
      wrapClassName='feedback-modal-wrap'
      destroyOnClose
      title={
        <div>
          <h2 style={{ margin: 0 }}>Feedback & ideas</h2>
        </div>
      }
    >
      <Form
        form={form}
        layout='vertical'
        requiredMark={false}
        onFinish={submitFeedback}
      >
        <Form.Item
          name='message'
          rules={[
            { required: true, message: 'Please describe your feedback.' },
            {
              validator: (_, value) => {
                const trimmed = (value ?? '').trim();
                if (trimmed.length < 10) {
                  return Promise.reject(new Error('Add at least 10 characters.'));
                }
                if (trimmed.length > 5000) {
                  return Promise.reject(new Error('Keep it under 5000 characters.'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea
            placeholder='Tell us what’s broken, confusing, or a feature you’d like.'
            autoSize={{ minRows: 4, maxRows: 8 }}
            maxLength={5000}
            showCount
            allowClear
          />
        </Form.Item>

        {!hasAuthedEmail ? (
          <Form.Item
            label={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Email (optional)</span>
                <Text type='secondary' style={{ fontWeight: 400 }}>
                  Helps us reach you when it’s resolved.
                </Text>
              </div>
            }
            name='email'
            rules={[{ type: 'email', message: 'Please enter a valid email.' }]}
          >
            <Input placeholder='email@example.com' />
          </Form.Item>
        ) : null}

        {error ? (
          <Alert
            type='error'
            showIcon
            message={error}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button onClick={closeModal} disabled={status === 'submitting'}>
            Cancel
          </Button>
          <Button type='primary' htmlType='submit' loading={status === 'submitting'}>
            {okText}
          </Button>
        </div>
      </Form>
    </Modal>
  );
});

FeedbackModal.displayName = 'FeedbackModal';
